import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { CONFIG } from '../config';

const AddMePage = ({ token }) => {
  // Event code step state (only used when no token provided)
  const [eventCodeStep, setEventCodeStep] = useState(!token);
  const [eventCode, setEventCode] = useState('');
  const [eventName, setEventName] = useState('');
  const [resolvedToken, setResolvedToken] = useState(token || '');
  const [validating, setValidating] = useState(false);
  const [allowedChannels, setAllowedChannels] = useState('both');

  const smsEnabled = allowedChannels === 'sms' || allowedChannels === 'both';
  const waEnabled = allowedChannels === 'whatsapp' || allowedChannels === 'both';

  // When a token is provided directly in the URL, fetch event info to get allowed_channels
  useEffect(() => {
    if (!token) return;
    fetch(`${CONFIG.apiBaseUrl}/public/event-by-token/${encodeURIComponent(token)}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        if (data.event_name) setEventName(data.event_name);
        const ch = data.allowed_channels || 'both';
        setAllowedChannels(ch);
        if (ch === 'sms') setFormData(prev => ({ ...prev, notification_preferences: ['sms'] }));
        else if (ch === 'whatsapp') setFormData(prev => ({ ...prev, notification_preferences: ['whatsapp'] }));
      })
      .catch(() => {});
  }, [token]);

  // Registration form state
  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
    notification_preferences: [], // 'whatsapp', 'sms'
    group: '', // 'Bride', 'Groom'
    consent: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNotificationChange = (type) => {
    setFormData(prev => ({
      ...prev,
      notification_preferences: prev.notification_preferences.includes(type)
        ? prev.notification_preferences.filter(t => t !== type)
        : [...prev.notification_preferences, type]
    }));
  };

  const handleGroupChange = (group) => {
    setFormData(prev => ({ ...prev, group }));
  };

  const handleEventCodeSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!eventCode.trim()) {
      setError('Please enter your event code');
      return;
    }

    try {
      setValidating(true);
      const resp = await fetch(`${CONFIG.apiBaseUrl}/public/validate-event-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_code: eventCode.trim() }),
      });

      const data = await resp.json();

      if (!resp.ok) {
        throw new Error(data.message || 'Event code not found');
      }

      setResolvedToken(data.token);
      setEventName(data.event_name);
      const eventChannels = data.allowed_channels || 'both';
      setAllowedChannels(eventChannels);
      // Auto-select the only channel if restricted
      if (eventChannels === 'sms') {
        setFormData(prev => ({ ...prev, notification_preferences: ['sms'] }));
      } else if (eventChannels === 'whatsapp') {
        setFormData(prev => ({ ...prev, notification_preferences: ['whatsapp'] }));
      }
      setEventCodeStep(false);
    } catch (err) {
      console.error('Event code validation failed:', err);
      setError(err.message || 'Event code not found. Please check that you have entered the correct event code.');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!formData.phone_number.trim()) {
      setError('Please enter your phone number');
      return;
    }
    if (smsEnabled && waEnabled && formData.notification_preferences.length === 0) {
      setError('Please select at least one notification preference (WhatsApp or SMS)');
      return;
    }
    if (!formData.group) {
      setError('Please select whether you are a guest of the Bride or Groom');
      return;
    }
    if (!formData.consent) {
      setError('Please agree to the consent statement to continue');
      return;
    }

    try {
      setSubmitting(true);

      // Build groups array from selections
      const groups = [formData.group];

      // Add notification preferences as groups for filtering
      // If single-channel event, auto-include that channel regardless of checkbox state
      const effectivePrefs = smsEnabled && waEnabled
        ? formData.notification_preferences
        : smsEnabled ? ['sms'] : ['whatsapp'];
      if (effectivePrefs.includes('whatsapp')) groups.push('WhatsApp');
      if (effectivePrefs.includes('sms')) groups.push('SMS');

      const payload = {
        name: formData.name.trim(),
        phone_number: formData.phone_number.trim(),
        groups: groups
      };

      const activeToken = resolvedToken;
      const endpoint = activeToken
        ? `${CONFIG.apiBaseUrl}/public/add-guest/${activeToken}`
        : `${CONFIG.apiBaseUrl}/public/add-guest`;

      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || data.message || 'Failed to register');
      }

      setSubmitted(true);
    } catch (err) {
      console.error('Registration failed:', err);
      setError(err.message || 'Failed to register. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="addme-page">
        <div className="addme-success">
          <h2>Thank You!</h2>
          <p>You have been successfully registered for event notifications.</p>
          <p>We'll send you updates via {formData.notification_preferences.join(' and ')}.</p>
        </div>
      </div>
    );
  }

  // Step 1: Event code entry (when no token provided)
  if (eventCodeStep) {
    return (
      <div className="addme-page">
        <div className="addme-container">
          <h2 className="addme-title">Join Our Guest List</h2>
          <p className="addme-subtitle">
            Enter the event code provided by your event organizer to get started.
          </p>

          <form onSubmit={handleEventCodeSubmit} className="addme-form">
            {error && (
              <div className="error-message">{error}</div>
            )}

            <div className="form-group">
              <label htmlFor="event_code" className="form-label">
                Event Code <span className="required">*</span>
              </label>
              <input
                type="text"
                id="event_code"
                value={eventCode}
                onChange={(e) => setEventCode(e.target.value.toUpperCase())}
                className="form-input"
                placeholder="e.g., SMITH2026"
                disabled={validating}
                autoFocus
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={validating || !eventCode.trim()}
            >
              {validating ? 'Checking...' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Step 2: Registration form
  return (
    <div className="addme-page">
      <div className="addme-container">
        <h2 className="addme-title">Join Our Guest List</h2>
        {eventName ? (
          <p className="addme-subtitle">Registering for: <strong>{eventName}</strong></p>
        ) : (
          <p className="addme-subtitle">
            Sign up to receive event notifications and updates.
          </p>
        )}

        <form onSubmit={handleSubmit} className="addme-form">
          {error && (
            <div className="error-message">{error}</div>
          )}

          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Your Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter your full name"
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone_number" className="form-label">
              Phone Number <span className="required">*</span>
            </label>
            <PhoneInput
              id="phone_number"
              defaultCountry="US"
              value={formData.phone_number}
              onChange={(value) => setFormData(prev => ({ ...prev, phone_number: value || '' }))}
              placeholder="(555) 123-4567"
              disabled={submitting}
            />
          </div>

          {(smsEnabled && waEnabled) && (
            <div className="form-group">
              <label className="form-label">
                How would you like to receive notifications? <span className="required">*</span>
              </label>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.notification_preferences.includes('whatsapp')}
                    onChange={() => handleNotificationChange('whatsapp')}
                    className="checkbox-input"
                    disabled={submitting}
                  />
                  <span>WhatsApp</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.notification_preferences.includes('sms')}
                    onChange={() => handleNotificationChange('sms')}
                    className="checkbox-input"
                    disabled={submitting}
                  />
                  <span>SMS Text Messages</span>
                </label>
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">
              Are you a guest of... <span className="required">*</span>
            </label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="guest_of"
                  checked={formData.group === 'Bride'}
                  onChange={() => handleGroupChange('Bride')}
                  className="radio-input"
                  disabled={submitting}
                />
                <span>The Bride</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="guest_of"
                  checked={formData.group === 'Groom'}
                  onChange={() => handleGroupChange('Groom')}
                  className="radio-input"
                  disabled={submitting}
                />
                <span>The Groom</span>
              </label>
            </div>
          </div>

          <div className="form-group consent-group">
            <label className="checkbox-label consent-label">
              <input
                type="checkbox"
                checked={formData.consent}
                onChange={() => setFormData(prev => ({ ...prev, consent: !prev.consent }))}
                className="checkbox-input"
                disabled={submitting}
              />
              <span>
                I agree and consent to subscribe to {allowedChannels === 'sms' ? 'SMS' : allowedChannels === 'whatsapp' ? 'WhatsApp' : 'SMS and/or WhatsApp'} notifications from Panda Notify at the number provided. I understand I will receive multiple messages per event. Data rates may apply. <br /><br/>You can respond with STOP to opt-out at any time. At the conclusion of the event, all personal data collected will be deleted. See our{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </span>
            </label>
          </div>

          {formData.consent && (
            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={submitting}
            >
              {submitting ? 'Registering...' : 'Sign Up for Notifications'}
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default AddMePage;
